import { useState, useEffect } from 'react'
import s from './OpportunitiesByQuoteNumber.scss'
import { makeReportsPostRequest, getReportsBaseUrl, makeGetRequest } from 'utils/api'
import { toUsers } from './serializers'
import { useLoading } from 'hooks'
import { getCurrency, getToken } from 'utils/authService'
import MaterialTable from '@material-table/core'
import { NumericFormat } from 'react-number-format'
import { RangeDatePicker, SwitchToggle } from 'components/ideal-core-ui'
import { utcDate, timezoneDate, currencyFormatter } from 'utils/helpers'
import { FaFileExcel, FaAngleRight, FaAngleDown, FaInfoCircle } from 'react-icons/fa'
import moment from 'moment'
import { AddButton } from 'components/common/AddButton'
import { Card, CardBody, CardHeader, Collapse, UncontrolledTooltip } from 'reactstrap'
import currency from 'currency.js'
import Select from 'react-select'
import { toTeams } from 'utils/serializers'
import { BID_SUBMISSION_STATUS } from 'utils/constants'
import { ExportCsv, ExportPdf } from '@material-table/exporters'

const columns = [
  {
    title: 'Quote #',
    field: 'quoteNumber',
    width: '150px',
    sorting: true,
    defaultSort: 'desc'
  },
  {
    title: 'Project #',
    field: 'projectNumber',
    width: '150px',
    sorting: true
  },
  {
    title: 'Opportunity Name',
    field: 'name',
    width: '150px',
    sorting: true,
    render: row => (
      <a href={`/opportunities/${row.id}`} target="_blank" rel="noreferrer">
        {row.name}
      </a>
    )
  },
  {
    title: 'Client(s)',
    field: 'companies',
    sorting: true,
    width: '200px',
    render: row => row.companies.join(', '),
    exportTransformer: row => row.companies.join(', ')
  },
  {
    title: 'Awarded To',
    field: 'awardedCompany',
    width: '200px',
    sorting: true
  },
  {
    title: 'Scope(s)',
    field: 'scopes',
    width: '250px',
    sorting: true,
    render: row => row.scopes?.join(', '),
    exportTransformer: row => row.scopes?.join(', ')
  },
  {
    title: 'Bid Due Date',
    field: 'bidDueDate',
    sorting: true,
    width: '120px',
    render: row => (row.bidDueDate ? timezoneDate(row.bidDueDate) : null),
    exportTransformer: row => (row.bidDueDate ? timezoneDate(row.bidDueDate) : null)
  },
  {
    title: 'Follow-up Date',
    field: 'bidFollowUpDate',
    sorting: true,
    width: '120px',
    render: row => (row.bidFollowUpDate ? timezoneDate(row.bidFollowUpDate) : null),
    exportTransformer: row => (row.bidFollowUpDate ? timezoneDate(row.bidFollowUpDate) : null)
  },
  {
    title: 'Opportunity Status',
    field: 'status',
    width: '200px',
    sorting: true
  },
  {
    title: 'Submission Date',
    field: 'bidSubmissionDate',
    sorting: true,
    width: '120px',
    render: row => (row.bidSubmissionDate ? timezoneDate(row.bidSubmissionDate) : null),
    exportTransformer: row => (row.bidSubmissionDate ? timezoneDate(row.bidSubmissionDate) : null)
  },
  {
    title: 'Submission Status',
    field: 'submissionStatus',
    sorting: true,
    width: '200px',
    render: row => BID_SUBMISSION_STATUS[row.submissionStatus],
    exportTransformer: row => BID_SUBMISSION_STATUS[row.submissionStatus]
  },
  {
    title: 'Bid Value',
    field: 'value',
    width: '120px',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.value}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.value })
  },
  {
    title: 'Won Value',
    field: 'wonValue',
    width: '120px',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.wonValue}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.wonValue })
  },
  {
    title: 'Team(s)',
    field: 'teams',
    width: '250px',
    sorting: true,
    render: row => row.teams?.join(', '),
    exportTransformer: row => row.teams?.join(', ')
  },
  {
    title: 'Opportunity Manager',
    field: 'manager',
    width: '150px',
    sorting: true
  }
]

const OpportunitiesByQuoteNumber = () => {
  const [result, setResult] = useState([])
  const [users, setUsers] = useState([])
  const [userFilter, setUserFilter] = useState([])
  const [teamFilter, setTeamFilter] = useState([])
  const [teams, setTeams] = useState([])
  const [, withLoadingLocks] = useLoading()
  const [collapse, setCollapse] = useState('')
  const [dealQuoteNrDateEnabled, setDealQuoteNrDateEnabled] = useState(false)
  const [dealQuoteNrDateFrom, setDealQuoteNrDateFrom] = useState(
    moment().subtract(2, 'weeks').startOf('day').format()
  )
  const [dealQuoteNrDateTo, setDealQuoteNrDateTo] = useState(
    moment().add(6, 'weeks').endOf('day').format()
  )

  const setDealQuoteNrDateFilters = (dates, utc = true) => {
    setDealQuoteNrDateFrom(utc ? utcDate(dates.from) : dates.from)
    setDealQuoteNrDateTo(utc ? utcDate(dates.to) : dates.to)
  }

  useEffect(() => {
    ;(async () => {
      const { content } = await makeGetRequest('/v1/users')
      setUsers(toUsers(content))

      const teams = await makeGetRequest('/v1/teams')
      setTeams(toTeams(teams))
    })()
  }, [])

  const refreshReport = async () => {
    const result = await withLoadingLocks(
      makeReportsPostRequest('/v1/deals/quote-number', {
        ...(dealQuoteNrDateEnabled
          ? {
              dealQuoteNrDateFrom: dealQuoteNrDateFrom,
              dealQuoteNrDateTo: dealQuoteNrDateTo
            }
          : {}),
        ...(userFilter && userFilter.length
          ? { userFilter: userFilter.map(user => user.value) }
          : {}),
        ...(teamFilter && teamFilter.length
          ? { teamFilter: teamFilter.map(user => user.value) }
          : {})
      })
    )
    setResult(result)
  }

  useEffect(() => {
    refreshReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealQuoteNrDateEnabled, dealQuoteNrDateFrom, dealQuoteNrDateTo, userFilter, teamFilter])

  const onBtnExport = () => {
    window.open(
      getReportsBaseUrl(
        `/v1/deals/quote-number/export?userFilter=${userFilter
          .map(user => user.value)
          .join()}&teamFilter=${teamFilter.map(user => user.value).join()}&token=${getToken()}${
          dealQuoteNrDateEnabled
            ? '&quoteNumberDateFrom=' +
              dealQuoteNrDateFrom +
              '&quoteNumberDateTo=' +
              dealQuoteNrDateTo
            : ''
        }`
      ),
      '_blank'
    )
  }

  const toggleCollapse = stage => {
    if (collapse !== stage) {
      setCollapse(stage)
    } else {
      setCollapse('')
    }
  }

  return (
    <>
      <h6 className={s.title}>Sales Reports &gt; Opportunities by Quote Number</h6>
      <div className={s.filters}>
        <div className={s.filter}>
          <SwitchToggle noText onToggle={setDealQuoteNrDateEnabled}>
            Date the Quote Number was created or last modified
          </SwitchToggle>
          <RangeDatePicker
            startDate={dealQuoteNrDateFrom}
            endDate={dealQuoteNrDateTo}
            onChange={setDealQuoteNrDateFilters}
            inputProps={{ disabled: !dealQuoteNrDateEnabled }}
          />
        </div>
        <Select
          onChange={values => setUserFilter(values)}
          options={users}
          value={userFilter}
          placeholder="Select User(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type1" style={{ position: 'relative', top: 34, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type1">
          To see all opportunities, please remove all users from selection.
        </UncontrolledTooltip>
        <Select
          onChange={values => setTeamFilter(values)}
          options={teams}
          value={teamFilter}
          placeholder="Select Team(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type2" style={{ position: 'relative', top: 34, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type2">
          To see all opportunities, please remove all teams from selection.
        </UncontrolledTooltip>
      </div>
      {Object.keys(result).length > 0 ? (
        <>
          <div className={s.table}>
            <AddButton className={s.exportExcel} onClick={onBtnExport}>
              <FaFileExcel className={s.excelIcon} />
              Export to Excel
            </AddButton>
            {Object.keys(result).map(stage => (
              <Card className={s.card} key={stage}>
                <CardHeader onClick={() => toggleCollapse(stage)} data-event={stage}>
                  <span className={s.arrow}>
                    {collapse === stage ? <FaAngleRight /> : <FaAngleDown />}
                  </span>
                  {stage} ({result[stage].count}) &#8226; Bid:{' '}
                  {currency(result[stage].totalBidValue, { symbol: getCurrency().symbol }).format()}{' '}
                  &#8226; Won:{' '}
                  {currency(result[stage].totalWonValue, {
                    symbol: getCurrency().symbol
                  }).format()}
                </CardHeader>
                <Collapse isOpen={collapse === stage}>
                  <CardBody>
                    {collapse === stage && (
                      <MaterialTable
                        title=""
                        key={stage}
                        columns={columns}
                        data={result[stage].deals}
                        options={{
                          tableLayout: 'fixed',
                          sorting: true,
                          headerStyle: {
                            backgroundColor: '#eee',
                            color: '#000',
                            fontSize: '14px'
                          },
                          pageSize: 10,
                          padding: 'dense',
                          pageSizeOptions: [
                            5,
                            10,
                            20,
                            50,
                            { value: result[stage].deals.length, label: 'All' }
                          ],
                          emptyRowsWhenPaging: false,
                          exportMenu: [
                            {
                              label: 'Export PDF',
                              exportFunc: (cols, datas) =>
                                ExportPdf(cols, datas, 'Opportunities by Quote Number')
                            },
                            {
                              label: 'Export CSV',
                              exportFunc: (cols, datas) =>
                                ExportCsv(cols, datas, 'Opportunities by Quote Number')
                            }
                          ],
                          columnsButton: true
                        }}
                        style={{
                          boxShadow: 'none',
                          fontSize: '13px'
                        }}
                      />
                    )}
                  </CardBody>
                </Collapse>
              </Card>
            ))}
          </div>
        </>
      ) : (
        <p className={s.noResults}>No results to display. Try a different time range.</p>
      )}
    </>
  )
}

export default OpportunitiesByQuoteNumber
